import type { CbTableState, HkTableState, TableState } from "../gen/bazivan/v1/table_pb";

/**
 * پرده‌ی شرط — نقشه‌اش و دروازه‌اش.
 *
 * وقتی میزِ آنلاین شروع می‌شود، پیش از پخشِ ورق یک پرده بالا می‌آید: دو سوی
 * میز با چهره و نام، «VS» در میانه، و — اگر میز ورودی داشته باشد — رگبارِ
 * سکه از کیفِ هر بازیکن به گلدانِ وسط. همان چیزی که بازی‌های میزیِ آنلاین
 * با آن شروع می‌شوند، و همان چیزی که به بازیکن می‌گوید **برای چه** بازی
 * می‌کند؛ عددِ ورودی پیش از این فقط یک خط در اتاق بود و سرِ میز دیده نمی‌شد.
 *
 * این فایل هیچ DOM نمی‌کشد. دو کار می‌کند:
 *
 *   ۱. **نقشه** ([[planFor]]) — از وضعیتِ میز درمی‌آورد که پرده لازم است یا
 *      نه، و اگر هست چه کسی کدام سو می‌نشیند و چند سکه پرواز می‌کند. تابعِ
 *      خالص است و آزمون دارد، چون شرطِ «لازم است یا نه» ظریف‌ترین تکه‌ی کار
 *      است: پرده باید **یک بار، سرِ شروع** بیاید و نه سرِ هر بازگشت از قطعی.
 *
 *   ۲. **دروازه** ([[introGate]]) — صف‌های صحنه پشتِ آن صبر می‌کنند. صحنه
 *      همان لحظه ساخته می‌شود و پیام‌ها را می‌گیرد (وگرنه رویدادِ وسطِ پرده
 *      گم می‌شد)، ولی کشیدنشان تا بالا رفتنِ پرده عقب می‌افتد.
 */

/** یک چهره‌ی روی پرده. */
export type IntroSeat = {
  seat: number;
  name: string;
  avatar: string;
  bot: boolean;
  subscriber: boolean;
  /** خودِ بازیکن — قابِ طلایی می‌گیرد. برای تماشاچی هیچ‌کس. */
  me: boolean;
};

export type IntroPlan = {
  tableId: string;
  /** ورودیِ هر بازیکن؛ ۰ = میزِ رایگان (پرده می‌آید، ولی بی‌سکه). */
  stake: bigint;
  /** گلدان = ورودی × شمارِ صندلی‌ها. */
  pot: bigint;
  /** سوی پایین — تیمِ خودم (یا در بازیِ تک‌نفره‌وار، فقط خودم). */
  mine: IntroSeat[];
  /** سوی بالا — بقیه. */
  theirs: IntroSeat[];
  /** چند سکه از کیفِ هر بازیکن پرواز کند. */
  coins: number;
  /** نمای تماشاچی: «من» نداریم، پس دو سو با نامِ تیم معرفی می‌شوند. */
  watching: boolean;
};

type AnyState = TableState | CbTableState | HkTableState;

const isHokm = (s: AnyState): s is TableState => "tricksWon" in s;
const isCb = (s: AnyState): s is CbTableState => "pileCounts" in s;
const isHk = (s: AnyState): s is HkTableState => "scores" in s;

const allZero = (xs: readonly number[]) => xs.every((v) => v === 0);

/**
 * میز **همین حالا** شروع شده؟
 *
 * پرده یک‌بارمصرف است و باید سرِ شروع بیاید. بازگشت از قطعیِ وسطِ دست هم یک
 * TableStateِ تازه است و اگر تنها معیارمان «اولین وضعیتِ این میز» باشد،
 * بازیکنی که وسطِ دستِ چهارم اینترنتش قطع و وصل شد، سه ثانیه پرده‌ی معارفه
 * می‌بیند و دستش را می‌بازد. پس از خودِ وضعیت می‌پرسیم هیچ‌چیز هنوز نشده.
 */
export function atMatchStart(s: AnyState): boolean {
  if (isHokm(s)) {
    return !s.trumpChosen && s.trick.length === 0 &&
      allZero(s.score) && allZero(s.tricksWon);
  }
  if (isCb(s)) return allZero(s.score) && allZero(s.pileCounts);
  if (isHk(s)) return allZero(s.scores) && s.rounds.length === 0;
  return false;
}

/**
 * چند سکه از هر کیف پرواز کند.
 *
 * شمارش نیست، وزن است: ۲۰۰۰ باید سنگین‌تر از ۱۰۰ *دیده* شود، ولی بیست سکه‌ی
 * ریز روی صفحه‌ی گوشی یک لکه‌ی طلایی است و نه رگبار.
 */
export const coinsFor = (stake: bigint): number =>
  stake >= 2000n ? 12 : stake >= 500n ? 10 : 7;

/**
 * نقشه‌ی پرده از وضعیتِ میز — یا `null` اگر پرده‌ای در کار نیست.
 *
 * تقسیمِ دو سو: بازیِ تیمی از `seatUnit`ِ خودِ سرور می‌آید (حکم و چهاربرگ)،
 * وگرنه «من پایین، بقیه بالا» — که برای هفت‌خبیثِ شش‌نفره هم همان چیزی است
 * که بازیکن سرِ میز می‌بیند.
 */
export function planFor(s: AnyState | null | undefined): IntroPlan | null {
  if (!s || !s.tableId || s.seats.length < 2) return null;
  if (!atMatchStart(s)) return null;

  const units = isHokm(s) ? s.seatUnit : [];
  const myUnit = units.length > s.me ? units[s.me] : undefined;
  const mineHas = (seat: number) =>
    myUnit !== undefined && units.length > seat ? units[seat] === myUnit : seat === s.me;

  const mine: IntroSeat[] = [], theirs: IntroSeat[] = [];
  s.seats.forEach((st, i) => {
    const row: IntroSeat = {
      seat: i,
      name: st.name || "بازیکن",
      avatar: st.avatar,
      bot: st.bot,
      subscriber: st.subscriber,
      me: !s.watching && i === s.me,
    };
    (mineHas(i) ? mine : theirs).push(row);
  });
  if (mine.length === 0 || theirs.length === 0) return null;

  const stake = s.stake ?? 0n;
  return {
    tableId: s.tableId,
    stake,
    // گلدان از شمارِ **آدم‌ها** و نه صندلی‌ها: ربات سرِ میزِ ورودی‌دار
    // می‌نشیند ولی پول نمی‌گذارد (آینه‌ی economy.StakeAllowed). با شمارِ
    // صندلی، پرده عددی نشان می‌داد که هیچ‌وقت پرداخت نمی‌شد.
    pot: stake * BigInt(s.seats.filter((x) => !x.bot).length),
    mine, theirs,
    coins: coinsFor(stake),
    watching: s.watching,
  };
}

// ───────────────────────── دروازه ─────────────────────────

/** پرده‌ی جاری: نقشه‌اش و وعده‌ای که صحنه‌ها پشتش ایستاده‌اند. */
let up: { plan: IntroPlan; promise: Promise<void>; open: () => void } | null = null;
/** میزی که پرده‌اش را داده و تمام شده — تا دوباره بالا نیاید. */
let served = "";

/**
 * نقشه را رزرو کن: برای میزِ تازه دروازه را می‌بندد و نقشه را می‌دهد؛ برای
 * میزی که پرده‌اش تمام شده `null`.
 *
 * **برای یک میز همیشه همان نقشه را برمی‌گردانَد و نه `null`.** این ظرافت
 * لازم است چون فراخوانش در رندرِ ری‌اکت است و ری‌اکت — در StrictMode و در
 * رندرِ دورانداختنی — تابعِ کامپوننت را دو بار صدا می‌زند. اگر بارِ دوم
 * `null` می‌داد، دروازه بسته می‌مانْد و هیچ پرده‌ای هم سوار نمی‌شد: میز
 * برای همیشه پشتِ پرده‌ای که وجود ندارد می‌ایستاد.
 */
export function armIntro(plan: IntroPlan | null): IntroPlan | null {
  if (!plan) return null;
  if (up && up.plan.tableId === plan.tableId) return up.plan;   // همان پرده، همان نقشه
  if (plan.tableId === served) return null;                     // پرده‌اش را داده
  // پرده‌ی میزِ قبلی اگر نیمه‌کاره ماند (بازیکن وسطش بیرون رفت)، همین‌جا رها
  // می‌شود: صفِ آن میز دیگر وجود ندارد، ولی وعده‌ی معلق هیچ‌وقت خوب نیست.
  up?.open();
  let open!: () => void;
  const promise = new Promise<void>((r) => { open = r; });
  up = { plan, promise, open };
  return plan;
}

/** صف‌های صحنه پشتِ این صبر می‌کنند — بی‌پرده، بی‌درنگ باز است. */
export const introGate = (): Promise<void> => up?.promise ?? Promise.resolve();

/** پرده بالا رفت (یا بازیکن ردش کرد): صحنه آزاد است. */
export function endIntro(): void {
  if (!up) return;
  served = up.plan.tableId;
  up.open();
  up = null;
}

/** فقط برای آزمون: دروازه و لچِ «این میز پرده‌اش را گرفته» را پاک کن. */
export function resetIntroForTest(): void {
  up?.open();
  up = null;
  served = "";
}
